import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { LitigationCaseRef, LitigationPosture } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const TERMINAL_STATUSES: LitigationPosture[] = ['SETTLED', 'WON', 'LOST', 'DISCONTINUED'];

export function LitigationDetail() {
  const { id } = useParams<{ id: string }>();
  const matterQ = useApiQuery<LitigationCaseRef>(id ? `/api/v1/m38/litigation-cases/${id}` : null);
  const [target, setTarget] = useState<LitigationPosture | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeStatus = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/litigation-cases/${id}/status`, { status: target });
      setTarget('');
      matterQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Status change failed');
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/litigation-cases/${id}/sync`);
      matterQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sync failed');
    } finally {
      setBusy(false);
    }
  };

  if (matterQ.loading) return <Spinner label="Loading matter…" />;
  if (matterQ.error) return <ErrorNote message={matterQ.error} />;
  const m = matterQ.data;
  if (!m) return null;

  return (
    <div>
      <PageHeader title={m.matterTitle} subtitle={m.mdaClient} actions={<StatusBadge status={m.status} />} />

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-2 text-sm font-bold text-slate-700">M37 Judiciary cross-reference</h2>
          {m.m37CaseNumber ? (
            <>
              <div className="text-sm text-slate-700">{m.m37CaseNumber}</div>
              <div className="mt-1 text-xs text-slate-500">
                Division {m.m37Division ?? '—'} · status {m.m37Status ?? '—'}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                {m.m37LastSyncedAt ? `Last synced ${formatDateTime(m.m37LastSyncedAt)}` : 'Never synced'}
              </div>
              <button className="btn-secondary mt-3" onClick={sync} disabled={busy}>
                {busy ? 'Syncing…' : 'Re-sync from M37'}
              </button>
            </>
          ) : (
            <p className="text-xs text-slate-400">No M37 case number linked — this matter is tracked ahead of e-filing.</p>
          )}
        </div>

        <div className="card p-5">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Details</h2>
          <div className="text-xs text-slate-500">Assigned counsel: {m.assignedCounselNin ?? '—'}</div>
          {m.notes ? <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{m.notes}</p> : null}
        </div>
      </div>

      {m.status === 'OPEN' ? (
        <div className="card flex flex-wrap items-end gap-2 p-4">
          <Field label="Move to terminal posture" className="!mb-0">
            <select className="field-input !w-auto" value={target} onChange={(e) => setTarget(e.target.value as LitigationPosture | '')}>
              <option value="">Select…</option>
              {TERMINAL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <button className="btn-primary" onClick={changeStatus} disabled={busy || !target}>
            Apply
          </button>
        </div>
      ) : null}
    </div>
  );
}
